'use client';
import React, { useEffect } from 'react';
import { NextPage } from 'next';

import { ErrorBoundary } from '@benzinga/core-ui';

import { checkDeviceType } from '@benzinga/device-utils';
import { useHydrate } from '@benzinga/hooks';

import { useIsUserEditor } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { useBenzingaEdge } from '@benzinga/edge';
import { comtexPublishers } from '@benzinga/article-manager';

import Error from '../Error/Error';

import { ArticleLayout } from './ArticleLayout';
import { ArticlePageProps } from './entities';
import { insertInContentBlocks, IN_CONTENT_SLOT_ID } from './insertInContentBlocks';
import { TrackingManager } from '@benzinga/tracking-manager';

const PlayStreamScript = React.lazy(() =>
  import('@benzinga/ads/playstream').then(module => {
    return { default: module.PlayStreamScript };
  }),
);

export const ArticlePageTemplate: NextPage<ArticlePageProps> = ({
  article,
  articleScrollViewMoreLink,
  baseUrl,
  campaigns,
  campaignSettings,
  campaignStrategy,
  deviceType,
  disablePartnerAdOnScroll = false,
  disablePaywall,
  disableWNSTNWidget,
  enablePlaystreamIndiaScript = false,
  errorCode,
  featuredTickers,
  followUpQuestions,
  googleNewsUrlKey = 'benzinga',
  hideSidebar = false,
  hideTopPanel,
  isTaggedPressRelease,
  isTemplate = false,
  isVideoArticle,
  layout,
  loadInfiniteArticles = true,
  loadMoreButtonVariant,
  nid,
  postedInVariant,
  queryParams,
  raptiveEnabled,
  shouldRenderLeadGenWidget,
  showApiText,
  showFontAwesomeIcons,
  showFullProHeadline,
  showWhatsAppIcon,
  wordCount,
}: ArticlePageProps) => {
  const session = React.useContext(SessionContext);
  const articleData = article ? insertInContentBlocks(article) : article;

  const isEditor = useHydrate(useIsUserEditor(), false);
  const { adLightEnabled: hasAdLight, isLoading: isAdLightLoading } = useBenzingaEdge();

  const isBot = useHydrate(checkDeviceType().isBot(), false);
  const trackedInContentNid = React.useRef<string | number | undefined>(undefined);
  const hasInContentSlot = Boolean(articleData?.blocks?.some(block => block?.tagAttributes?.id === IN_CONTENT_SLOT_ID));

  useEffect(() => {
    if (isAdLightLoading) return;
    if (hasAdLight || !hasInContentSlot) return;
    if (trackedInContentNid.current === nid) return;
    trackedInContentNid.current = nid;
    session.getManager(TrackingManager).trackCampaignEvent('view', {
      label: layout?.in_content?.title ?? '',
      partner_id: `Widget Id: - ${layout?.in_content?.id}`,
      unit_type: 'leadgen-in-article',
    });
  }, [hasAdLight, hasInContentSlot, isAdLightLoading, layout?.in_content?.id, layout?.in_content?.title, nid, session]);

  React.useEffect(() => {
    // Reload page
    // https://upmostly.com/tutorials/settimeout-in-react-components-using-hooks
    const timer = setTimeout(
      () => {
        // eslint-disable-next-line no-restricted-globals
        location.reload();
      },
      (Math.floor(Math.random() * 11) + 22) * 60 * 1000,
    );
    return () => clearTimeout(timer);
  }, []);

  if (article && comtexPublishers.includes(article?.type)) {
    return (<Error statusCode={410} />) as React.ReactElement;
  }

  if (article?.type === 'benzinga_partner') {
    return (<Error statusCode={404} />) as React.ReactElement;
  }

  if (!articleData) {
    return (<Error statusCode={errorCode ?? 404} />) as React.ReactElement;
  }

  //console.log('Completing build for article page: ' + nid);

  return (
    <>
      {enablePlaystreamIndiaScript ? <PlayStreamScript scriptType="article" /> : null}
      <ErrorBoundary name="article-layout">
        <ArticleLayout
          {...{
            articleData,
            articleScrollViewMoreLink,
            baseUrl,
            campaignSettings,
            campaignStrategy,
            campaigns,
            deviceType: deviceType || null,
            disablePartnerAdOnScroll,
            disablePaywall,
            disableWNSTNWidget,
            executePageviewEvent: false,
            executeReplaceHistory: false,
            featuredTickers,
            followUpQuestions,
            googleNewsUrlKey,
            hasAdLight,
            hideSidebar,
            hideTopPanel,
            isBot,
            isDraft: false,
            isEditor,
            isTaggedPressRelease,
            isTemplate,
            isVideoArticle,
            layout,
            loadInfiniteArticles: loadInfiniteArticles,
            loadMoreButtonVariant,
            postedInVariant,
            queryParams,
            raptiveEnabled,
            shouldRenderLeadGenWidget,
            showApiText,
            showFontAwesomeIcons,
            showFullProHeadline,
            showPartnerAd: true,
            showWhatsAppIcon,
            wordCount: wordCount ?? undefined,
          }}
        />
      </ErrorBoundary>
    </>
  );
};
